import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateBox } from "devextreme-react/date-box";
import DataGrid, {
  Column,
  ColumnChooser,
  ColumnFixing,
  GroupPanel,
  Grouping,
  HeaderFilter,
  MasterDetail,
  Paging,
  Scrolling,
  SearchPanel,
  Sorting,
  Summary,
  TotalItem,
} from "devextreme-react/data-grid";
import saveAs from "file-saver";
import * as ExcelJS from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./PositionsPositions.css";
import { baseUrl } from "../../hooks/useFetch";
import useCobDate from "../../hooks/useCobDate";
import { toDate, fmt, fmtUK } from "../../utils/dateUtils";

interface Row {
  trade_id?: string | number;
  parent_trade_id?: string | number;
  child_count?: number;
  __rid?: string;
  year?: string | number;
  quarter?: string | number;
  period?: string;
  raw_trades?: boolean;
  class?: string;
  book?: string;
  owner?: string;
  product_type?: string;
  risk_pod?: string;
  risk_sub_pod?: string;
  assettype?: string;
  assetcategory?: string;
  assetclass?: string;
  underlying?: string;
  put_call?: string;
  strike_price?: number | string;
  gamma?: number;
  vega?: number;
  theta?: number;
  notional_eur?: number;
  delta?: number;
  [k: string]: unknown;
}

const numberCellRender = ({ value }: { value: any }): React.ReactElement => {
  if (value == null || value === "" || Number.isNaN(Number(value))) {
    return <span>{(value as any) ?? ""}</span>;
  }
  const n = Number(value);
  if (n < 0) {
    const formatted = Math.abs(n).toLocaleString("en-GB", {
      maximumFractionDigits: 0,
    });
    return <span style={{ color: "red" }}>({formatted})</span>;
  }
  return (
    <span>
      {n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}
    </span>
  );
};

// Detail panel — fetches the children of a parent row on first expand, and
// caches the result so re-expanding the same row is free. Children are rows
// whose parent_trade_id equals THIS parent's trade_id.
const PositionDetail: React.FC<{
  parent: Row;
  cobDate: Date | null;
  cache: Map<string, Row[]>;
}> = ({ parent, cobDate, cache }) => {
  const parentTradeId =
    parent.trade_id != null ? String(parent.trade_id) : "";
  const cobStr = cobDate ? String(fmt(cobDate)) : "";
  const cacheKey = `${cobStr}::${parentTradeId}`;
  const cached = cache.get(cacheKey);

  const [rows, setRows] = useState<Row[] | null>(cached ?? null);
  const [err, setErr] = useState<string>("");

  useEffect(() => {
    if (!parentTradeId || !cobStr) return;
    if (cache.has(cacheKey)) {
      setRows(cache.get(cacheKey) || []);
      return;
    }
    const ac = new AbortController();
    const qs = new URLSearchParams({
      cob_date: cobStr,
      parent_trade_id: parentTradeId,
    });
    fetch(`${baseUrl}/positions_exposure_children?${qs.toString()}`, {
      signal: ac.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(({ data }: { data: Row[] }) => {
        const list = data || [];
        cache.set(cacheKey, list);
        setRows(list);
      })
      .catch((e: any) => {
        if (e?.name !== "AbortError") setErr(e?.message || "Failed to load");
      });
    return () => ac.abort();
  }, [parentTradeId, cobStr, cacheKey, cache]);

  if (err) {
    return (
      <div style={{ padding: 8, color: "crimson", fontSize: 12 }}>{err}</div>
    );
  }
  if (rows === null) {
    return (
      <div style={{ padding: 8, opacity: 0.7, fontSize: 12 }}>
        Loading children…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{ padding: 8, opacity: 0.7, fontSize: 12 }}>
        No children for this parent.
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "6px 12px 10px 24px",
        background: "transparent",
      }}
    >
      <div
        style={{
          fontSize: 11,
          marginBottom: 6,
          color: "#6b7280",
          letterSpacing: 0.2,
        }}
      >
        {rows.length.toLocaleString()} child trade
        {rows.length === 1 ? "" : "s"}
      </div>
      <DataGrid
        dataSource={rows}
        keyExpr="trade_id"
        showBorders={false}
        showColumnLines={false}
        showRowLines
        rowAlternationEnabled
        columnAutoWidth
        allowColumnResizing
        wordWrapEnabled={false}
        onCellPrepared={(e: any) => {
          if (e.rowType !== "data") return;
          const f = e.column?.dataField;
          const numeric = [
            "notional_eur",
            "delta",
            "gamma",
            "vega",
            "theta",
            "strike_price",
          ];
          if (
            numeric.includes(f) &&
            Number.isFinite(e.value) &&
            e.value < 0
          ) {
            e.cellElement.classList.add("neg");
          }
        }}
      >
        <Column dataField="trade_id" caption="TradeID" />
        <Column dataField="parent_trade_id" caption="ParentTradeID" />
        <Column dataField="book" caption="Book" />
        <Column dataField="product_type" caption="ProductType" />
        <Column dataField="underlying" caption="Underlying" />
        <Column dataField="period" caption="Month" />
        <Column dataField="put_call" caption="Put/Call" />
        <Column
          dataField="strike_price"
          caption="Strike"
          dataType="number"
        />
        <Column
          dataField="delta"
          caption="Delta"
          dataType="number"
          cellRender={numberCellRender}
        />
        <Column
          dataField="notional_eur"
          caption="Notional (EUR)"
          dataType="number"
          cellRender={numberCellRender}
        />
      </DataGrid>
    </div>
  );
};

const PositionsFlatGrid: React.FC = () => {
  const gridRef = useRef<any>(null);

  const [cobDate, setCobDate] = useState<Date | null>(null);
  const [, , , latestCobDate] = useCobDate("/positions_latest_cob");
  const [compareDate, setCompareDate] = useState<Date | null>(null);

  const [positionData, setPositionData] = useState<Row[]>([]);
  const [compareData, setCompareData] = useState<Row[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string>("");

  const [mode, setMode] = useState<"current" | "diff">("current");

  // Cache of children fetched per (cob_date, parent_trade_id) so re-expanding
  // an aggregate row never re-hits the server.
  const childrenCacheRef = useRef<Map<string, Row[]>>(new Map());

  const prevCob = useRef<Date | null>(null);
  const prevCmp = useRef<Date | null>(null);
  const swappingRef = useRef<boolean>(false);

  // Initial load to discover the latest CoB date
  useEffect(() => {
    if (cobDate !== null) return;

    const ac = new AbortController();
    setLoading(true);
    setErr("");

    (async () => {
      try {
        const r = await fetch(
          `${baseUrl}/positions_exposure`,
          { signal: ac.signal }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const { data, cob_date } = await r.json();

        setCobDate(toDate(cob_date));
        setPositionData(data || []);
        setCompareDate(null);
      } catch (e: any) {
        if (e?.name !== "AbortError") setErr(e?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [cobDate]);

  // Switch between current and diff mode based on date changes
  useEffect(() => {
    if (!cobDate) return;

    if (swappingRef.current) {
      // During swap, both dates change at once but we want to stay in diff mode
      prevCob.current = cobDate;
      prevCmp.current = compareDate;
      if (compareDate) setMode("diff");
      return;
    }

    if (
      !prevCob.current ||
      cobDate.getTime() !== prevCob.current.getTime()
    ) {
      setMode("current");
      prevCob.current = cobDate;
    } else if (
      compareDate &&
      (!prevCmp.current ||
        compareDate.getTime() !== prevCmp.current.getTime())
    ) {
      setMode("diff");
      prevCmp.current = compareDate;
    }
  }, [cobDate, compareDate]);

  const didInit = useRef<boolean>(false);

  // Reload current data when CoB date changes
  useEffect(() => {
    if (cobDate == null) return;

    if (!didInit.current) {
      didInit.current = true;
      document.body.style.cursor = "";
      return;
    }

    const ac = new AbortController();
    document.body.style.cursor = "wait";
    setLoading(true);
    setErr("");

    (async () => {
      try {
        const qs = new URLSearchParams({
          cob_date: fmt(cobDate) as string,
        });
        const r = await fetch(
          `${baseUrl}/positions_exposure?${qs.toString()}`,
          { signal: ac.signal }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const { data } = await r.json();
        setPositionData(data || []);
      } catch (e: any) {
        if (e?.name !== "AbortError") setErr(e?.message || "Failed to load");
      } finally {
        setLoading(false);
        document.body.style.cursor = "";
      }
    })();

    return () => {
      ac.abort();
      document.body.style.cursor = "";
    };
  }, [cobDate]);

  const didMount = useRef<boolean>(false);

  // Load diff data when compareDate is set
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }

    if (!compareDate || compareDate === cobDate) return;

    const ac = new AbortController();
    document.body.style.cursor = "wait";
    setLoading(true);
    setErr("");

    const qs = new URLSearchParams({
      cob_date_start: fmt(cobDate) as string,
      cob_date_end: fmt(compareDate) as string,
    });

    fetch(`${baseUrl}/positions_exposure_diff?${qs.toString()}`, {
      signal: ac.signal,
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(({ data }: { data: Row[] }) => setCompareData(data || []))
      .catch((e: any) => {
        if (e?.name !== "AbortError") setErr(e?.message || "Failed to load");
      })
      .finally(() => {
        setLoading(false);
        document.body.style.cursor = "";
      });

    return () => {
      ac.abort();
      document.body.style.cursor = "";
    };
  }, [compareDate, cobDate]);

  const activeStore = mode === "current" ? positionData : compareData;

  // The grid shows aggregated rows at the top level; children are fetched on
  // demand when a row is expanded. We just need a stable per-row key.
  const topLevelData = useMemo(() => {
    const rows: Row[] = Array.isArray(activeStore) ? activeStore : [];
    return rows.map(
      (r, idx): Row => ({ ...r, __rid: `${r.trade_id ?? "x"}-${idx}` })
    );
  }, [activeStore]);

  const fetchStatusText = useMemo(() => {
    if (loading) return "Loading…";
    if (err) return err;
    const total = topLevelData.length;
    const expandable = topLevelData.filter(
      (r) => typeof r.child_count === "number" && r.child_count > 1
    ).length;
    return expandable > 0
      ? `Loaded (${total.toLocaleString()} positions · ${expandable.toLocaleString()} expandable)`
      : `Loaded (${total.toLocaleString()} positions)`;
  }, [loading, err, topLevelData]);

  const renderDetail = useCallback(
    (props: any): React.ReactElement => {
      const parent: Row = props?.data?.data || {};
      return (
        <PositionDetail
          parent={parent}
          cobDate={cobDate}
          cache={childrenCacheRef.current}
        />
      );
    },
    [cobDate]
  );

  const doExportExcel = async (): Promise<void> => {
    const inst = gridRef.current?.instance?.();
    if (!inst) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Positions");

    await exportDataGrid({
      component: inst,
      worksheet,
      autoFilterEnabled: true,
      customizeCell: ({ gridCell, excelCell }: any) => {
        if (!gridCell) return;
        if (gridCell.rowType === "data") {
          const v = gridCell.value;
          if (typeof v === "number" && Number.isFinite(v)) {
            excelCell.value = v;
            excelCell.numFmt = "#,##0;[Red](#,##0)";
            if (v < 0) {
              excelCell.font = {
                ...(excelCell.font || {}),
                color: { argb: "FFFF0000" },
              };
            }
          }
        }
      },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "positions_flat.xlsx");
  };

  const doExportPdf = (): void => {
    const inst = gridRef.current?.instance?.();
    if (!inst) return;

    const cols = (inst.getVisibleColumns() as any[]).filter(
      (c) => c.dataField && c.visible !== false
    );
    const rows = (inst.getVisibleRows() as any[]).filter(
      (r) => r.rowType === "data"
    );

    const head = [cols.map((c) => c.caption || c.dataField)];
    const body = rows.map((r) =>
      cols.map((c) => {
        const v = r.data?.[c.dataField];
        if (v == null || v === "") return "";
        const num = Number(v);
        if (Number.isFinite(num) && c.dataType === "number") return num;
        if (typeof v === "boolean") return v ? "✓" : "";
        return String(v);
      })
    );

    const doc = new jsPDF("landscape", "pt", "a4");
    const d = new Date().toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    const title =
      mode === "diff" && cobDate && compareDate
        ? `Positions Δ ${fmtUK(cobDate)} → ${fmtUK(compareDate)}`
        : `Positions ${d}`;
    doc.text(title, 20, 25);

    (doc as any).autoTable({
      head,
      body,
      styles: { fontSize: 7, cellPadding: 2, overflow: "linebreak" },
      headStyles: {
        fontStyle: "bold",
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
      },
      didParseCell: ({ section, cell }: any) => {
        if (section === "body" && typeof cell.raw === "number") {
          const n = cell.raw;
          if (n < 0) {
            cell.styles.textColor = [255, 0, 0];
            cell.styles.fontStyle = "bold";
            cell.text = [
              `(${Math.abs(n).toLocaleString("en-GB", {
                maximumFractionDigits: 0,
              })})`,
            ];
          } else {
            cell.text = [
              n.toLocaleString("en-GB", { maximumFractionDigits: 0 }),
            ];
          }
          cell.styles.halign = "right";
        }
      },
      margin: { top: 40, left: 20, right: 20, bottom: 20 },
    });

    doc.save("positions_flat.pdf");
  };

  const onCellPrepared = (e: any): void => {
    if (e.rowType !== "data") return;

    // Hide the master-detail expand icon for rows with no underlying raw trades.
    if (e.column?.command === "expand") {
      const r = e.data as Row | undefined;
      const cc = typeof r?.child_count === "number" ? r.child_count : 0;
      if (cc <= 1) {
        e.cellElement.innerHTML = "";
        e.cellElement.style.cursor = "default";
      }
      return;
    }

    const f = e.column?.dataField;
    if (!f) return;
    const numeric = ["notional_eur", "delta", "gamma", "vega", "theta"];
    if (numeric.includes(f)) {
      const v = e.value;
      if (Number.isFinite(v) && v < 0) {
        e.cellElement.classList.add("neg");
      }
    }
  };

  // Click-and-drag horizontal panning on the grid body
  useEffect(() => {
    const inst = gridRef.current?.instance?.();
    if (!inst) return;

    let detachDom: (() => void) | null = null;

    const wire = (): void => {
      const el = inst.element() as HTMLElement | undefined;
      if (!el) return;
      const scroller = el.querySelector(
        ".dx-datagrid-rowsview .dx-scrollable-container"
      ) as HTMLElement | null;
      if (!scroller) return;

      detachDom?.();

      let isDown = false;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      const THRESHOLD = 4;

      const isInteractive = (target: EventTarget | null): boolean => {
        if (!(target instanceof HTMLElement)) return false;
        return !!target.closest(
          "input, button, select, textarea, a, .dx-checkbox, .dx-texteditor, .dx-button, .dx-link"
        );
      };

      const onDown = (e: MouseEvent): void => {
        if (e.button !== 0) return;
        if (isInteractive(e.target)) return;
        isDown = true;
        dragging = false;
        startX = e.pageX;
        startScroll = scroller.scrollLeft;
      };

      const onMove = (e: MouseEvent): void => {
        if (!isDown) return;
        const dx = e.pageX - startX;
        if (!dragging && Math.abs(dx) > THRESHOLD) {
          dragging = true;
          scroller.style.cursor = "grabbing";
          document.body.style.userSelect = "none";
        }
        if (dragging) {
          e.preventDefault();
          scroller.scrollLeft = startScroll - dx;
        }
      };

      const onUp = (): void => {
        if (!isDown) return;
        isDown = false;
        if (dragging) {
          scroller.style.cursor = "grab";
          document.body.style.userSelect = "";
        }
        dragging = false;
      };

      const onClickCapture = (e: MouseEvent): void => {
        // Suppress click-through after a drag
        if (dragging) {
          e.stopPropagation();
          e.preventDefault();
        }
      };

      scroller.style.cursor = "grab";
      scroller.addEventListener("mousedown", onDown);
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
      scroller.addEventListener("click", onClickCapture, true);

      detachDom = () => {
        scroller.removeEventListener("mousedown", onDown);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        scroller.removeEventListener("click", onClickCapture, true);
        scroller.style.cursor = "";
        document.body.style.userSelect = "";
      };
    };

    wire();
    inst.on("contentReady", wire);

    return () => {
      try {
        inst.off("contentReady", wire);
      } catch {}
      detachDom?.();
    };
  }, []);

  const swapDates = (): void => {
    if (!compareDate || !cobDate) return;
    swappingRef.current = true;
    const a = cobDate;
    setCobDate(compareDate);
    setCompareDate(a);
    setTimeout(() => {
      swappingRef.current = false;
    }, 0);
  };

  return (
    <div className="dashboard-container">
      <div className="filters-row">
        <div className="filter-block">
          <div className="filters-group">
            <label>CoB:</label>
            <DateBox
              displayFormat="dd/MM/yyyy"
              useMaskBehavior
              pickerType="calendar"
              value={cobDate}
              onValueChanged={(e: any) => {
                if (!e.event) return;
                setCobDate(e.value);
                if (!swappingRef.current) {
                  setCompareDate(null);
                  setMode("current");
                }
              }}
              width={150}
              max={latestCobDate ?? undefined}
            />
          </div>

          <div className="filters-group">
            <label>Comparison:</label>
            <DateBox
              displayFormat="dd/MM/yyyy"
              useMaskBehavior
              pickerType="calendar"
              value={compareDate}
              onValueChanged={(e: any) => setCompareDate(e.value)}
              width={150}
              max={latestCobDate ?? undefined}
            />
          </div>

          <div className="filters-group" style={{ gap: 8 }}>
            <button
              onClick={swapDates}
              disabled={!compareDate || !cobDate}
              style={{
                padding: "4px 10px",
                fontSize: 12,
                borderRadius: 4,
                border: "1px solid #ccc",
                background: "#f7f7f7",
                cursor:
                  !compareDate || !cobDate ? "not-allowed" : "pointer",
              }}
            >
              Swap dates
            </button>
            <button
              onClick={() => {
                setCompareDate(null);
                setMode("current");
              }}
              disabled={!compareDate}
              style={{
                padding: "4px 10px",
                fontSize: 12,
                borderRadius: 4,
                border: "1px solid #ccc",
                background: "#f7f7f7",
                cursor: !compareDate ? "not-allowed" : "pointer",
              }}
            >
              Clear comparison
            </button>
          </div>
        </div>

        <div className="filter-block">
          <div className="filters-group" style={{ marginTop: 8 }}>
            <div
              style={{
                fontSize: 12,
                opacity: err ? 1 : 0.75,
                color: err ? "crimson" : "inherit",
              }}
            >
              {fetchStatusText}
            </div>

            {mode === "current" && cobDate ? (
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                COB: {fmtUK(cobDate)}
              </div>
            ) : null}

            {mode === "diff" && cobDate && compareDate ? (
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Δ {fmtUK(cobDate)} → {fmtUK(compareDate)}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="positions-pivot-section">
        <DataGrid
          ref={gridRef}
          dataSource={topLevelData}
          keyExpr="__rid"
          showBorders
          rowAlternationEnabled
          allowColumnReordering
          allowColumnResizing
          columnAutoWidth
          columnResizingMode="widget"
          wordWrapEnabled={false}
          height="80vh"
          onCellPrepared={onCellPrepared}
          onToolbarPreparing={(e: any) => {
            const items = e.toolbarOptions.items as any[];
            const exportItems = [
              {
                location: "after",
                widget: "dxButton",
                options: {
                  text: "EXPORT EXCEL",
                  type: "success",
                  stylingMode: "contained",
                  onClick: doExportExcel,
                },
              },
              {
                location: "after",
                widget: "dxButton",
                options: {
                  text: "EXPORT PDF",
                  type: "danger",
                  stylingMode: "contained",
                  onClick: doExportPdf,
                },
              },
            ];
            const idx = items.findIndex(
              (i) => i.name === "columnChooserButton"
            );
            if (idx >= 0) items.splice(idx, 0, ...exportItems);
            else items.push(...exportItems);
          }}
        >
          <SearchPanel visible width={260} placeholder="Search…" />
          <HeaderFilter visible allowSearch />
          <Sorting mode="multiple" />
          <GroupPanel visible />
          <Grouping autoExpandAll={false} />
          <ColumnChooser enabled mode="select" />
          <ColumnFixing enabled />
          <Scrolling mode="virtual" />
          <Paging enabled={false} />
          <MasterDetail enabled component={renderDetail} />

          <Column dataField="trade_id" caption="TradeID" />
          <Column dataField="parent_trade_id" caption="ParentTradeID" />
          <Column dataField="year" caption="Year" dataType="string" />
          <Column dataField="quarter" caption="Quarter" dataType="string" />
          <Column dataField="period" caption="Month" dataType="string" />
          <Column dataField="class" caption="Class" />
          <Column dataField="book" caption="Book" />
          <Column dataField="owner" caption="Owner" />
          <Column dataField="product_type" caption="ProductType" />
          <Column dataField="risk_pod" caption="RiskPod" />
          <Column dataField="risk_sub_pod" caption="RiskSubPod" />
          <Column dataField="assettype" caption="AssetType" />
          <Column dataField="assetcategory" caption="AssetCategory" />
          <Column dataField="assetclass" caption="AssetClass" />
          <Column dataField="underlying" caption="Underlying" />
          <Column dataField="put_call" caption="Put/Call" />
          <Column
            dataField="strike_price"
            caption="Strike"
            dataType="number"
          />
          <Column
            dataField="gamma"
            caption="Gamma"
            dataType="number"
            cellRender={numberCellRender}
          />
          <Column
            dataField="vega"
            caption="Vega"
            dataType="number"
            cellRender={numberCellRender}
          />
          <Column
            dataField="theta"
            caption="Theta"
            dataType="number"
            cellRender={numberCellRender}
          />
          <Column
            dataField="raw_trades"
            caption="RawTrades"
            dataType="boolean"
            visible={false}
          />
          <Column
            dataField="notional_eur"
            caption={
              mode === "diff"
                ? "Δ Notional (EUR)"
                : "Notional (EUR)"
            }
            dataType="number"
            cellRender={numberCellRender}
          />
          <Column
            dataField="delta"
            caption={mode === "diff" ? "Δ Delta" : "Delta"}
            dataType="number"
            cellRender={numberCellRender}
          />

          <Summary>
            <TotalItem
              column="notional_eur"
              summaryType="sum"
              valueFormat={{ type: "fixedPoint", precision: 0 }}
              displayFormat="{0}"
            />
            <TotalItem
              column="delta"
              summaryType="sum"
              valueFormat={{ type: "fixedPoint", precision: 0 }}
              displayFormat="{0}"
            />
            <TotalItem
              column="year"
              summaryType="count"
              displayFormat="{0} rows"
            />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
};

export default PositionsFlatGrid;
